import React from 'react';
import { Stack, Box, IconButton, Button } from '@mui/material';
import { ContentCopy as ContentCopyIcon, Videocam as VideocamIcon } from '@mui/icons-material';

interface VideoLinkSectionProps {
  videoUrl: string;
  videoProvider?: string;
  labelStyle?: React.CSSProperties | object;
}

export default function VideoLinkSection({
  videoUrl,
  videoProvider,
  labelStyle = {}
}: VideoLinkSectionProps) {
  const getProviderDisplayName = (provider?: string) => {
    switch (provider) {
      case 'zoom':
        return 'Zoom';
      case 'google_meet':
        return 'Google Meet';
      case 'teams':
        return 'Teams';
      default:
        return 'Video';
    }
  };

  const handleCopyVideoLink = async () => {
    if (videoUrl) {
      try {
        await navigator.clipboard.writeText(videoUrl);
      } catch (err) {
        console.error('Failed to copy video link:', err);
      }
    }
  };

  const handleJoinMeeting = () => {
    if (videoUrl) {
      window.open(videoUrl, '_blank');
    }
  };

  if (!videoUrl) {
    return null;
  }

  return (
    <Stack sx={{ gap: '8px' }}>
      <Stack
        sx={{
          color: '#989898',
          fontSize: '14px',
          fontFamily: 'Source Sans Pro, sans-serif',
          ...labelStyle
        }}
      >
        Video link
      </Stack>
      <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
        <Box
          component="a"
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#084d6d',
            fontSize: '14px',
            fontFamily: 'Source Sans Pro, sans-serif',
            textDecoration: 'underline',
            textDecorationPosition: 'from-font',
            cursor: 'pointer',
            '&:hover': {
              color: '#063b52'
            }
          }}
        >
          {videoUrl}
        </Box>
        <IconButton
          aria-label="copy video link"
          onClick={handleCopyVideoLink}
          size="small"
          sx={{
            padding: 0,
            minWidth: 'auto',
            width: '16px',
            height: '16px',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <ContentCopyIcon sx={{ color: '#333333', fontSize: '16px' }} />
        </IconButton>
      </Stack>
      <Button
        variant="outlined"
        onClick={handleJoinMeeting}
        startIcon={
          <VideocamIcon
            sx={{
              width: '20px',
              height: '20px',
              color: '#084d6d'
            }}
          />
        }
        sx={{
          backgroundColor: '#def3f2',
          border: 'none',
          color: '#084d6d',
          textTransform: 'none',
          alignSelf: 'flex-start',
          padding: '12px 20px 12px 12px',
          fontSize: '14px',
          fontWeight: 700,
          fontFamily: 'Source Sans Pro, sans-serif',
          borderRadius: '6px',
          boxShadow: 'none',
          height: '44px',
          '&:hover': {
            backgroundColor: '#d4eeec',
            border: 'none',
            boxShadow: 'none'
          }
        }}
      >
        Join {getProviderDisplayName(videoProvider)} Meeting
      </Button>
    </Stack>
  );
}
